import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { fetchAndCacheSuppliers, getSupplierNameByIdSync } from '../data/suppliers';

interface AuthContextType {
  token: string;
  tenantId: string;
  allowedActions: string[];
  loading: boolean;
  error: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

const PERMISSIONS_API_DIRECT =
  'https://incentive-app.dev.qaraservices.net/api/sheets/user-access-resources';
const PERMISSIONS_API_PROXY = '/api/incentive/sheets/user-access-resources';

const fetchPermissions = async (token: string, tenantId: string): Promise<string[]> => {
  const headers = {
    'tenant-id': tenantId,
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };

  try {
    const response = await axios.get(PERMISSIONS_API_DIRECT, { headers });
    return response.data?.userPermissionActions || [];
  } catch (err: any) {
    // No response property means a network/CORS error — retry via nginx proxy
    if (!err.response) {
      const proxyResponse = await axios.get(PERMISSIONS_API_PROXY, { headers });
      return proxyResponse.data?.userPermissionActions || [];
    }
    throw err;
  }
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [token, setToken] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [allowedActions, setAllowedActions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const init = async () => {
      try {
        // 1. Resolve credentials: prefer URL params, fall back to sessionStorage (for refreshes)
        const params = new URLSearchParams(window.location.search);
        const resolvedToken =
          params.get('token') || sessionStorage.getItem('auth_token') || '';
        const resolvedTenantId =
          params.get('tanantId') || sessionStorage.getItem('tenant_id') || '';

        if (!resolvedToken || !resolvedTenantId) {
          setError(
            'Missing authentication parameters. Please access this page from the admin panel.'
          );
          setLoading(false);
          return;
        }

        // 2. Persist to sessionStorage for page refreshes (token stays in-memory/session only)
        sessionStorage.setItem('auth_token', resolvedToken);
        sessionStorage.setItem('tenant_id', resolvedTenantId);

        // If the tenant changed since the last session, drop the cached supplier name so a
        // failed/slow lookup below can't leave a previous tenant's name in localStorage
        // (name-based endpoints, e.g. wallet transactions, would silently query the wrong supplier).
        const previousTenantId = localStorage.getItem('tenant_id');
        if (previousTenantId && previousTenantId !== resolvedTenantId) {
          localStorage.removeItem('excel_supplier_name');
        }

        // 3. Store tenantId in localStorage so api.service.ts can use it as supplierId
        localStorage.setItem('tenant_id', resolvedTenantId);

        setToken(resolvedToken);
        setTenantId(resolvedTenantId);

        // 4. Fetch permissions and warm the suppliers cache in parallel
        const [permissionActions] = await Promise.all([
          fetchPermissions(resolvedToken, resolvedTenantId),
          // Warm cache so getSupplierNameByIdSync works for any supplier, then store name
          fetchAndCacheSuppliers()
            .then(() => {
              const supplierName = getSupplierNameByIdSync(resolvedTenantId);
              if (supplierName) {
                // Feeds name-based endpoints (reset-logs, gifts, etc.)
                localStorage.setItem('excel_supplier_name', supplierName);
              }
            })
            .catch(() => {
              // Non-fatal: name-based endpoints fall back to existing localStorage value
            }),
        ]);

        setAllowedActions(permissionActions);
      } catch (err: any) {
        setError(
          err?.response?.data?.message ||
            err?.message ||
            'Failed to load permissions. Please try again.'
        );
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  return (
    <AuthContext.Provider value={{ token, tenantId, allowedActions, loading, error }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
